import React, { FC, useCallback, useContext, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import Button from '../../../components/bootstrap/Button';
import { useToasts } from 'react-toast-notifications';
import Toasts from '../../../components/bootstrap/Toasts';
import { resetPassword, getCountry, resendOtp } from '../../../apis';
import AuthContext from '../../../contexts/authContext';
import Spinner from '../../../components/bootstrap/Spinner';
import { useFormik } from 'formik';
import ConfigContext from '../../../contexts/configContext';
import useDarkMode from '../../../hooks/useDarkMode';
import PhoneInput from 'react-phone-input-2';
import Checks from '../../../components/bootstrap/forms/Checks';
import 'react-phone-input-2/lib/style.css';
import { EXCLUDED_PHONE_COUNTRIES } from '../../../constants/countryFilters';
const ResetPassword: FC = () => {
	const { darkModeStatus } = useDarkMode();
	const { addToast } = useToasts();
	const location = useLocation();
	const resetPasswordState = location.state as {
		userName?: string;
		usernameMode?: 'mobile' | 'text';
	} | null;
	const [countryCode, setCountryCode] = useState<string>('');
	const [usernameMode, setUsernameMode] = useState<'mobile' | 'text'>(
		resetPasswordState?.usernameMode ||
			(process.env.REACT_APP_HIDE_USERNAME === 'true' ? 'mobile' : 'text'),
	);

	const navigate = useNavigate();
	useEffect(() => {
		const fetchCountryCode = async () => {
			try {
				const response = await getCountry();
				const detectedCountryCode =
					response?.country || response?.country_code || response?.countryCode;
				if (detectedCountryCode) {
					const normalizedCode = detectedCountryCode.toUpperCase();
					const country_code =
						process.env.REACT_APP_STATIC_COUNTRY_CODE || normalizedCode;
					setCountryCode(country_code);
				}
			} catch (error) {
				console.warn('Unable to detect country automatically', error);
				setCountryCode('IN');
			}
		};

		fetchCountryCode();
	}, []);
	const disableCountrySelect =
		process.env.REACT_APP_STATIC_COUNTRY_CODE !== undefined &&
		process.env.REACT_APP_STATIC_COUNTRY_CODE !== '';
	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			username: resetPasswordState?.userName || '',
			resetToken: '',
			newPassword: '',
			confirmPassword: '',
		},
		validate: (values) => {
			const errors: {
				username?: string;
				resetToken?: string;
				newPassword?: string;
				confirmPassword?: string;
			} = {};

			if (!values.username) {
				errors.username = 'Required';
			}
			if (usernameMode === 'mobile' && values.username.length < 7) {
				errors.username = 'Minimum of 7 digits needed.';
			}
			if (!values.resetToken) {
				errors.resetToken = 'Required';
			}
			if (!values.newPassword) {
				errors.newPassword = 'Required';
			}
			if (!values.confirmPassword) {
				errors.confirmPassword = 'Required';
			}

			return errors;
		},
		validateOnChange: false,
		onSubmit: async (values) => {
			setIsLoading(true);
			const arkbaseurl = process.env.REACT_APP_ARKAPIURL;
			let data = null;
			data = await resetPassword(arkbaseurl, {
				confirmPassword: values.confirmPassword,
				password: values.newPassword,
				token: values.resetToken,
				username: values.username,
			});

			// console.log("res==",data)
			if (data.success === true) {
				setIsLoading(false);
				navigate('/auth-pages/login');
				addToast(
					<Toasts title='Suceess' icon='VerifiedUser' iconColor='warning' isDismiss>
						Reset password has been done successfully.
					</Toasts>,
					{
						autoDismiss: true,
					},
				);
			} else {
				setIsLoading(false);
				addToast(
					<Toasts title='Failed' icon='Error' time='Now' isDismiss>
						{data.message}
					</Toasts>,
					{
						autoDismiss: true,
					},
				);
			}
		},
	});

	const [isLoading, setIsLoading] = useState<boolean>(false);

	const handleReturnToLogin = useCallback(() => navigate('/auth-pages/login'), [navigate]);

	const handleKeyPress = (e: any) => {
		if (e.key === 'Enter') {
			formik.handleSubmit();
		}
	};
	const handleResendCode = async () => {
		const username = formik.values.username.trim();
		if (!username) {
			addToast(
				<Toasts title='Error' icon='Error' iconColor='danger' isDismiss>
					Mobile number is required to resend OTP
				</Toasts>,
				{ autoDismiss: true },
			);
			return;
		}

		const data = await resendOtp(process.env.REACT_APP_ARKAPIURL, username, 2);
		if (data.success === true) {
			addToast(
				<Toasts title='Success' icon='CheckCircle' iconColor='success' isDismiss>
					OTP Sent Successfully
				</Toasts>,
				{ autoDismiss: true },
			);
		} else {
			addToast(
				<Toasts title='Error' icon='Error' iconColor='danger' isDismiss>
					{data?.message || 'Failed to resend OTP'}
				</Toasts>,
				{ autoDismiss: true },
			);
		}
	};
	const backgroundColor = darkModeStatus ? '#212529' : '#f8f9fa';
	const borderColor = darkModeStatus ? '#343a40' : '#f8f9fa';
	const isMobileMode = usernameMode === 'mobile';

	return (
		<PageWrapper isProtected={false} title={'Login'} className={'bg-dark'}>
			<Page className='p-0'>
				<div className='row h-100 align-items-center justify-content-center'>
					<div className='col-xl-4 col-lg-6 col-md-8 shadow-3d-container'>
						<Card
							className='shadow-3d-dark'
							data-tour='login-page'
							style={{ backgroundColor: '#2a2d36' }}>
							<CardBody
								className={classNames('rounded-3', {
									'bg-l10-dark': !darkModeStatus,
									'bg-l1-dark': darkModeStatus,
								})}>
								<div className='text-center my-5'>
									<Link
										to='/auth-pages/resetpassword'
										className={
											'text-decoration-none  fw-bold display-2 text-dark'
										}>
										<img src='/logo192.png' width={150} />
									</Link>
								</div>

								<div
									className={classNames('text-center', 'h1', 'fw-bold', 'mt-5', {
										'text-dark': !darkModeStatus,
										'text-white': darkModeStatus,
									})}>
									Reset your password,
								</div>
								<div className='text-center h4 text-muted mb-5'>
									We'll{' '}
									{isMobileMode
										? 'send you OTP to your mobile'
										: 'email you instructions'}{' '}
									to reset the password.
								</div>

								<form className='row g-4'>
									<div className='col-12'>
										<div
											className={classNames(
												'd-flex',
												'align-items-center justify-content-center',
												'gap-2',
												{
													'text-dark': !darkModeStatus,
													'text-white': darkModeStatus,
												},
											)}>
											<span className='small text-muted'>
												{isMobileMode
													? 'Use Email instead'
													: 'Use Mobile instead'}
											</span>
											<Checks
												id='resetPasswordUsernameMode'
												type='switch'
												name='resetPasswordUsernameMode'
												ariaLabel='Username input type switch'
												checked={isMobileMode}
												onChange={() => {
													setUsernameMode((prev) =>
														prev === 'mobile' ? 'text' : 'mobile',
													);
													formik.setErrors({});
												}}
											/>
										</div>
									</div>
									<div className='col-12'>
										{isMobileMode ? (
											<div className='col-12'>
												<FormGroup
													id='username'
													isFloating
													style={{ backgroundColor: 'transparent' }}>
													<PhoneInput
														country={countryCode.toLowerCase()}
														excludeCountries={EXCLUDED_PHONE_COUNTRIES}
														placeholder='Enter Mobile number'
														value={formik.values.username}
														onBlur={() =>
															formik.setFieldTouched('username', true)
														}
														disableCountryCode={disableCountrySelect}
														disableCountryGuess={disableCountrySelect}
														disableDropdown={disableCountrySelect}
														onFocus={() => {
															formik.setErrors({});
														}}
														onChange={(value) =>
															formik.setFieldValue('username', value)
														}
														inputStyle={{
															width: '100%',
															height: 'calc(3.5rem + 2px)',
															borderRadius: '1rem',
															border: '1px solid #f8f9fa',
															backgroundColor: backgroundColor,
															borderColor: borderColor,
														}}
													/>
													<Input
														type='hidden'
														autoComplete='phoneNumber'
														value={formik.values.username}
														isTouched={formik.touched.username}
														invalidFeedback={formik.errors.username}
														isValid={formik.isValid}
														onChange={formik.handleChange}
														onBlur={formik.handleBlur}
														onFocus={() => {
															formik.setErrors({});
														}}
													/>
												</FormGroup>
											</div>
										) : (
											<div className='col-12'>
												<FormGroup
													id='username'
													isFloating
													label='Username'>
													<Input
														autoComplete='username'
														value={formik.values.username}
														isTouched={formik.touched.username}
														invalidFeedback={formik.errors.username}
														isValid={formik.isValid}
														onChange={formik.handleChange}
														onBlur={formik.handleBlur}
														onFocus={() => {
															formik.setErrors({});
														}}
													/>
												</FormGroup>
											</div>
										)}
									</div>
									<div className='col-12'>
										<FormGroup
											id='resetToken'
											isFloating
											label={isMobileMode ? 'OTP' : 'Reset Token'}>
											<Input
												type='text'
												value={formik.values.resetToken}
												isTouched={formik.touched.resetToken}
												invalidFeedback={formik.errors.resetToken}
												isValid={formik.isValid}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												onFocus={() => {
													formik.setErrors({});
												}}
											/>
										</FormGroup>
									</div>
									<div className='col-12'>
										<FormGroup id='newPassword' isFloating label='New Password'>
											<Input
												type='password'
												value={formik.values.newPassword}
												isTouched={formik.touched.newPassword}
												invalidFeedback={formik.errors.newPassword}
												isValid={formik.isValid}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												onFocus={() => {
													formik.setErrors({});
												}}
											/>
										</FormGroup>
									</div>
									<div className='col-12'>
										<FormGroup
											id='confirmPassword'
											isFloating
											label='Confirm Password'>
											<Input
												type='password'
												value={formik.values.confirmPassword}
												isTouched={formik.touched.confirmPassword}
												invalidFeedback={formik.errors.confirmPassword}
												isValid={formik.isValid}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												onFocus={() => {
													formik.setErrors({});
												}}
												onKeyDown={handleKeyPress}
											/>
										</FormGroup>
									</div>

									<div
										className={classNames(
											'col-12',
											'd-flex',
											'justify-content-end',
											{
												'text-dark': !darkModeStatus,
												'text-white': darkModeStatus,
											},
										)}>
										<span
											style={{ cursor: 'pointer' }}
											onClick={handleReturnToLogin}>
											Return to Login
										</span>
									</div>

									<div className='col-12'>
										{isMobileMode && (
											<Button
												color='info'
												isOutline
												className='w-100 py-3 mb-3'
												onClick={handleResendCode}>
												Resend Code
											</Button>
										)}
										<Button
											color='info'
											className='w-100 py-3'
											onClick={formik.handleSubmit}>
											{isLoading && <Spinner isSmall inButton isGrow />}
											Reset Password
										</Button>
									</div>
								</form>
							</CardBody>
						</Card>
					</div>
				</div>
			</Page>
		</PageWrapper>
	);
};

export default ResetPassword;
